import {Map, List} from 'immutable';
import {
  BULK_ORDER_QUANTITY_THRESHOLD,
  INTL_BULK_INCREMENT_PERCENTAGE,
  INTL_SINGLES_INCREMENT_PERCENTAGE
} from '~/constants/QuoteConstants';

export function deserializeQuoteWidget(data) {
  const items = data.items || [];
  const buyMorePrice = data.buy_more_price;
  const buyMoreQuantity = data.buy_more_qty;
  const buyMoreUnitPrice = data.buy_more_unit_price;
  const decorationMethod = data.decoration_method;
  const errors = data.errors;
  const ncoEligible = data.discountEligible;
  const priceGrid = List(data.price_grid);
  const service_charges = List(data.service_charges);
  const totalPrice = data.total_price || data.totalPrice || data.price;
  const volumeDiscountPercentage = parseFloat(data.volume_discount_percentage);
  const volumeDiscountPrice = parseFloat(data.price_before_volume_discount);
  const volumeDiscountSavings = parseFloat(data.volume_discount_savings);
  const itemizedPrices = items.reduce((acc, item) => {
    const productId = String(parseInt(item.productId || item.product_id, 10));
    return acc.set(
      productId,
      Map({
        itemPrice: parseFloat(item.unitPrice || item.unit_price),
        subtotal: parseFloat(item.totalPrice || item.price)
      })
    );
  }, Map());
  const caveats = List(data.caveats);
  return {
    buyMorePrice,
    buyMoreQuantity,
    buyMoreUnitPrice,
    caveats,
    decorationMethod,
    errors,
    itemizedPrices,
    ncoEligible,
    priceGrid,
    totalPrice: totalPrice && parseFloat(totalPrice),
    service_charges,
    shipping: deserializeShipping(data),
    volumeDiscountPercentage,
    volumeDiscountPrice,
    volumeDiscountSavings
  };
}

export function deserializePriceQuote(data) {
  const items = data.items || [];
  const totalPrice = data.totalPrice || data.price;
  const ncoEligible = data.discountEligible;
  const itemizedPrices = items.reduce((acc, item) => {
    const productId = String(parseInt(item.productId || item.product_id, 10));
    return acc.set(
      productId,
      Map({
        itemPrice: parseFloat(item.unitPrice || item.unit_price),
        subtotal: parseFloat(item.totalPrice || item.price)
      })
    );
  }, Map());
  return {
    itemizedPrices,
    totalPrice: totalPrice && parseFloat(totalPrice),
    ncoEligible
  };
}

export function deserializeOnlyItemizedPricesFromPriceQuote(data) {
  if (!data?.itemizedPrices) {
    return null;
  }

  const itemizedPrices = data.itemizedPrices
    .map(itemPrice => {
      if (!itemPrice) {
        return null;
      }
      const subtotal = itemPrice.get('subtotal');
      const price = itemPrice.get('itemPrice');

      if (!subtotal || !price) {
        return null;
      }

      return new Map({
        itemPrice: parseFloat(price),
        subtotal: parseFloat(subtotal)
      });
    })
    .filter(item => item !== null);

  return itemizedPrices;
}

export function deserializeForInternational(data, totalQuantity) {
  const items = data.items || [];
  let priceGrid = data.price_grid || [];

  const totalPrice = incrementPrice(
    parseFloat(data.total_price),
    totalQuantity
  );
  const priceBeforeDiscount = incrementPrice(
    parseFloat(data.price_before_volume_discount),
    totalQuantity
  );
  const volumeDiscountPct = parseFloat(data.volume_discount_percentage);

  const itemizedPrices = items.reduce((acc, item) => {
    const productId = String(parseInt(item.productId || item.product_id, 10));
    const itemPrice = incrementPrice(
      parseFloat(item.unitPrice || item.unit_price),
      totalQuantity
    );
    const subtotal = incrementPrice(
      parseFloat(item.totalPrice || item.price),
      totalQuantity
    );
    acc[productId] = {
      itemPrice,
      subtotal
    };
    return acc;
  }, {});

  const itemizedSizeAndQtyByUnitPrice = items.reduce((acc, item) => {
    const productId = String(parseInt(item.productId || item.product_id, 10));
    acc[productId] = item.size_and_quantities_by_unit_price.map(
      sizeQtyUnitPrice => {
        const [sizeQty, unitPrice] = sizeQtyUnitPrice.split('$');
        const incUnitPrice = incrementPrice(
          parseFloat(unitPrice),
          totalQuantity
        ).toFixed(2);
        return `${sizeQty}$${incUnitPrice}`;
      }
    );
    return acc;
  }, {});

  const itemUnitPrices = Object.values(itemizedPrices).map(
    itemizedPrice => itemizedPrice.itemPrice
  );

  const minUnitPrice = Math.min.apply(Math, itemUnitPrices);
  const maxUnitPrice = Math.max.apply(Math, itemUnitPrices);

  priceGrid = priceGrid.reduce((acc, priceInfo) => {
    acc.push({
      quantity: priceInfo.quantity,
      subtotal: incrementPrice(
        parseFloat(priceInfo.bmsm_price),
        priceInfo.quantity
      ),
      unitPrice: incrementPrice(
        parseFloat(priceInfo.bmsm_unit_price),
        priceInfo.quantity
      )
    });
    return acc;
  }, []);

  return {
    decoType: data.decoration_method,
    itemizedPrices,
    itemizedSizeAndQtyByUnitPrice,
    minUnitPrice,
    maxUnitPrice,
    priceBeforeDiscount,
    priceGrid,
    shippingCaveat: data.shipping_caveat,
    totalPrice,
    volumeDiscountPct
  };
}

function incrementPrice(price, totalQuantity) {
  const markupPct =
    totalQuantity > BULK_ORDER_QUANTITY_THRESHOLD
      ? INTL_BULK_INCREMENT_PERCENTAGE
      : INTL_SINGLES_INCREMENT_PERCENTAGE;
  return price + price * (markupPct / 100);
}

function deserializeShipping(data) {
  const shippingData = Map({
    city: data.city,
    // ppsSuggested: data.pps_suggested, // Not sure what this was, but does not appear to be used
    shippingCaveat: Map(data.shipping_caveat),
    state: data.state,
    turnAdjustment: data.turn_adjustment
  });

  if (data.standard_shipping) {
    shippingData.standardDelivery = Map({
      turnTime: data.standard_shipping.turn_time,
      deliveryDate: data.standard_shipping.delivery_date
    });
  }

  if (data.rush_shipping) {
    shippingData.rushDelivery = Map({
      charge: data.rush_shipping.charge,
      deliveryDate: data.rush_shipping.delivery_date,
      deliveryDay: data.rush_shipping.delivery_day,
      shortDate: data.rush_shipping.short_date,
      turnTime: data.rush_shipping.turn_time
    });
  }

  if (data.super_rush_shipping) {
    shippingData.superRushDelivery = Map({
      charge: data.super_rush_shipping.charge,
      deliveryDate: data.super_rush_shipping.delivery_date,
      deliveryDay: data.super_rush_shipping.delivery_day,
      orderByDeadline: data.super_rush_shipping.order_by_deadline,
      shortDate: data.super_rush_shipping.short_date,
      turnTime: data.super_rush_shipping.turn_time
    });
  }

  return shippingData;
}
